import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core, Decorative } from "@faire/design-tokens";
import { InsiderBadge } from "@faire/retailer-visitor-shared/components/InsiderBadge/InsiderBadge";
import { useInsiderFeatureEligibility } from "@faire/retailer-visitor-shared/hooks/insider/useInsiderFeatureEligibility";
import { useInsiderHelpCenterArticleLink } from "@faire/retailer-visitor-shared/hooks/insider/useInsiderHelpCenterArticleLink";
import { useInsiderMembershipInformation } from "@faire/retailer-visitor-shared/hooks/insider/useInsiderMembershipInformation";
import { useInsiderMembershipPrice } from "@faire/retailer-visitor-shared/hooks/insider/useInsiderMembershipPrice";
import { useInsiderTrialingPeriod } from "@faire/retailer-visitor-shared/hooks/insider/useInsiderTrialingPeriod";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { moneyOrZero, useFormatMoney } from "@faire/web--source/common/money";
import { Button } from "@faire/web--source/slate/Button";
import { Insider } from "@faire/web--source/slate/icons/InsiderIcon";
import { LoadingSpinner } from "@faire/web--source/slate/LoadingSpinner";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { useQuery as useIsEligibleForAnnualBillingQuery } from "@faire/web-api--source/api/retailer/insider/is-eligible-for-annual-billing/get-hooks";
import { useQuery as useInsiderMarketingPageData } from "@faire/web-api--source/api/retailer/insider/marketing-page/get-hooks";
import { QueryParameters } from "@faire/web-api--source/routes/support/articles/articleId";
import supportSelfServe from "@faire/web-api--source/routes/support/self-serve";
import React from "react";
import styled from "styled-components";
var getLinkFormatting = function(sectionNumber) {
    return function getLinkFormatting(chunks) {
        var helpCenterLink = useInsiderHelpCenterArticleLink(sectionNumber ? QueryParameters.build({
            section: sectionNumber
        }) : undefined);
        return /*#__PURE__*/ _jsx(LinkButton, {
            linkTo: helpCenterLink,
            children: chunks
        });
    };
};
export var CoverageChangesAnswer = function() {
    return /*#__PURE__*/ _jsx(Typography, {
        children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "NFVbL2",
            defaultMessage: [
                {
                    type: 0,
                    value: "You'll have Insider free shipping on a largely consistent selection of brands, but we refresh Insider free shipping coverage quarterly so we can offer free shipping on as many orders as possible. Each quarter you'll gain free shipping with brands, and some quarters you'll see your free shipping expire on select brands (typically under 5% of your free shipping will expire). We'll notify you of upcoming changes two weeks before each refresh so you can plan your buying. "
                },
                {
                    type: 8,
                    value: "LinkFormatting",
                    children: [
                        {
                            type: 0,
                            value: "Learn more"
                        }
                    ]
                }
            ],
            values: {
                LinkFormatting: getLinkFormatting("3")
            }
        })
    });
};
export var PhoneSupportAnswer = function() {
    return /*#__PURE__*/ _jsx(Typography, {
        children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "rOkIU8",
            defaultMessage: [
                {
                    type: 0,
                    value: "As part of your Insider membership, you'll get exclusive access to Faire phone support. If you ever want to talk through issues or questions with a Faire team member, all you have to do is visit the "
                },
                {
                    type: 8,
                    value: "SelfServeSupportLinkFormatting",
                    children: [
                        {
                            type: 0,
                            value: "Contact us page"
                        }
                    ]
                },
                {
                    type: 0,
                    value: " and select the button to request a call from Faire."
                }
            ],
            values: {
                SelfServeSupportLinkFormatting: SelfServeSupportLinkFormatting
            }
        })
    });
};
export var HowDoesFaireSelectBrandsAnswer = function() {
    return /*#__PURE__*/ _jsx(Typography, {
        children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "vJFE3q",
            defaultMessage: [
                {
                    type: 0,
                    value: "Each Insider is eligible for free shipping on a personalized selection of brands. Faire determines eligibility based on the estimated cost to ship between you and a given brand. We use your location and the brand's prior reported shipping costs in our estimate. Please note, there is no way to request that a specific brand be included in your Insider membership. "
                },
                {
                    type: 8,
                    value: "LinkFormatting",
                    children: [
                        {
                            type: 0,
                            value: "Learn more"
                        }
                    ]
                }
            ],
            values: {
                LinkFormatting: getLinkFormatting("3")
            }
        })
    });
};
export var HowToFindInsiderBrandsQuestion = function() {
    var hasFreeDutyAdvertising = useInsiderFeatureEligibility().hasFreeDutyAdvertising;
    return /*#__PURE__*/ _jsx(Typography, {
        variant: "subheadingSansRegular",
        children: hasFreeDutyAdvertising ? /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "bi/k/I",
            defaultMessage: [
                {
                    type: 0,
                    value: "How do I find brands with Insider free shipping and import duties on Faire?"
                }
            ]
        }) : /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "LcDvYD",
            defaultMessage: [
                {
                    type: 0,
                    value: "How do I find brands with Insider free shipping on Faire?"
                }
            ]
        })
    });
};
export var HowToFindInsiderBrandsTwoThresholdsAnswer = function() {
    var _insiderMarketingPageData_logged_out_info_threshold_values, _insiderMarketingPageData_logged_out_info, _insiderMarketingPageData_logged_out_info_threshold_values1, _insiderMarketingPageData_logged_out_info1;
    var isLoggedInRetailer = useIsLoggedInRetailer();
    var _useInsiderMarketingPageData = useInsiderMarketingPageData({
        enabled: !isLoggedInRetailer
    }), insiderMarketingPageData = _useInsiderMarketingPageData.data, isLoading = _useInsiderMarketingPageData.isLoading;
    var formatMoney = useFormatMoney().formatMoney;
    var loggedOutInsiderThresholdT1 = insiderMarketingPageData === null || insiderMarketingPageData === void 0 ? void 0 : (_insiderMarketingPageData_logged_out_info = insiderMarketingPageData.logged_out_info) === null || _insiderMarketingPageData_logged_out_info === void 0 ? void 0 : (_insiderMarketingPageData_logged_out_info_threshold_values = _insiderMarketingPageData_logged_out_info.threshold_values) === null || _insiderMarketingPageData_logged_out_info_threshold_values === void 0 ? void 0 : _insiderMarketingPageData_logged_out_info_threshold_values.THRESHOLD_1;
    var loggedOutInsiderThresholdT2 = insiderMarketingPageData === null || insiderMarketingPageData === void 0 ? void 0 : (_insiderMarketingPageData_logged_out_info1 = insiderMarketingPageData.logged_out_info) === null || _insiderMarketingPageData_logged_out_info1 === void 0 ? void 0 : (_insiderMarketingPageData_logged_out_info_threshold_values1 = _insiderMarketingPageData_logged_out_info1.threshold_values) === null || _insiderMarketingPageData_logged_out_info_threshold_values1 === void 0 ? void 0 : _insiderMarketingPageData_logged_out_info_threshold_values1.THRESHOLD_2;
    var _useInsiderMembershipInformation = useInsiderMembershipInformation(), _useInsiderMembershipInformation_thresholds = _useInsiderMembershipInformation.thresholds, threshold1 = _useInsiderMembershipInformation_thresholds.threshold1, threshold2 = _useInsiderMembershipInformation_thresholds.threshold2;
    if (isLoading) {
        return /*#__PURE__*/ _jsx(LoadingSpinner, {});
    }
    return /*#__PURE__*/ _jsx(Typography, {
        children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "hNOzY9",
            defaultMessage: [
                {
                    type: 0,
                    value: "Look for the Insider key "
                },
                {
                    type: 1,
                    value: "insiderIconToken"
                },
                {
                    type: 0,
                    value: " or use the Insider filter while you're shopping. You'll see an Insider qualifying order amount ("
                },
                {
                    type: 1,
                    value: "thresholdT1Money"
                },
                {
                    type: 0,
                    value: " or "
                },
                {
                    type: 1,
                    value: "thresholdT2Money"
                },
                {
                    type: 0,
                    value: ") on each eligible brand."
                }
            ],
            values: {
                insiderIconToken: /*#__PURE__*/ _jsx(StyledInsider, {
                    small: true,
                    color: Decorative.icon.teal,
                    fill: Decorative.icon.teal,
                    fontSize: "14px"
                }),
                thresholdT1Money: formatMoney({
                    money: moneyOrZero(isLoggedInRetailer ? threshold1 : loggedOutInsiderThresholdT1)
                }),
                thresholdT2Money: formatMoney({
                    money: moneyOrZero(isLoggedInRetailer ? threshold2 : loggedOutInsiderThresholdT2)
                })
            }
        })
    });
};
export var HowToFindInsiderBrandsAnswer = function(param) {
    var _param_sectionNumber = param.sectionNumber, sectionNumber = _param_sectionNumber === void 0 ? "2" : _param_sectionNumber;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var formatMoney = useFormatMoney().formatMoney;
    var _useInsiderMembershipInformation = useInsiderMembershipInformation(), _useInsiderMembershipInformation_thresholds = _useInsiderMembershipInformation.thresholds, thresholdValue = _useInsiderMembershipInformation_thresholds.threshold1;
    var hasFreeDutyAdvertising = useInsiderFeatureEligibility().hasFreeDutyAdvertising;
    if (thresholdValue) {
        var freeShippingThresholdMoney = formatMoney({
            money: moneyOrZero(thresholdValue),
            omitCentsIfZero: true
        });
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsxs(Typography, {
                    children: [
                        /*#__PURE__*/ _jsx(StrictLocalMsg, {
                            id: "D6dNrr",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "Look for the Insider key or use the Insider filter while you're shopping. You'll see two types of coverage"
                                }
                            ]
                        }),
                        ":"
                    ]
                }),
                /*#__PURE__*/ _jsx(Spacer, {
                    height: "2x"
                }),
                /*#__PURE__*/ _jsx(InsiderBadge, {
                    showFreeShipping: true,
                    showFreeDuties: hasFreeDutyAdvertising,
                    shouldUseFallbackText: true
                }),
                /*#__PURE__*/ _jsx(Spacer, {
                    height: "1x"
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    children: hasFreeDutyAdvertising ? /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "PbazHi",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Most brands with free shipping and import duties will have this label, which means you do not have a spend minimum to unlock Insider free shipping and import duties."
                            }
                        ]
                    }) : /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "g6JpgE",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Most brands with free shipping will have this label, which means you do not have a spend minimum to unlock Insider free shipping."
                            }
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(Spacer, {
                    height: "2x"
                }),
                /*#__PURE__*/ _jsx(InsiderBadge, {
                    insiderBadgeText: strictLocalize({
                        id: "tcaVd9",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Free shipping over "
                            },
                            {
                                type: 1,
                                value: "freeShippingThresholdMoney"
                            }
                        ]
                    }, {
                        freeShippingThresholdMoney: freeShippingThresholdMoney
                    })
                }),
                /*#__PURE__*/ _jsx(Spacer, {
                    height: "1x"
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    children: hasFreeDutyAdvertising ? /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "lQp92Z",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "You'll also have access to free shipping and import duties on an additional set of brands when your order is over "
                            },
                            {
                                type: 1,
                                value: "freeShippingThresholdMoney"
                            },
                            {
                                type: 0,
                                value: "."
                            }
                        ],
                        values: {
                            freeShippingThresholdMoney: freeShippingThresholdMoney
                        }
                    }) : /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "FnFJ1g",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "You'll also have access to free shipping on an additional set of brands when your order is over "
                            },
                            {
                                type: 1,
                                value: "freeShippingThresholdMoney"
                            },
                            {
                                type: 0,
                                value: "."
                            }
                        ],
                        values: {
                            freeShippingThresholdMoney: freeShippingThresholdMoney
                        }
                    })
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsx(Typography, {
        children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "4mAFfv",
            defaultMessage: [
                {
                    type: 0,
                    value: "To find brands and products that offer Insider free shipping, simply look for the Insider key "
                },
                {
                    type: 1,
                    value: "insiderIconToken"
                },
                {
                    type: 0,
                    value: " while shopping. You can also filter your shopping experience so you only see products with Insider free shipping. To do so, find the Insider filter at the top of the category and click to filter. "
                },
                {
                    type: 8,
                    value: "LinkFormatting",
                    children: [
                        {
                            type: 0,
                            value: "Learn more"
                        }
                    ]
                }
            ],
            values: {
                insiderIconToken: /*#__PURE__*/ _jsx(StyledInsider, {
                    small: true,
                    color: Decorative.icon.teal,
                    fill: Decorative.icon.teal,
                    fontSize: "14px"
                }),
                LinkFormatting: getLinkFormatting(sectionNumber)
            }
        })
    });
};
export var ShippingCostsAnswer = function() {
    return /*#__PURE__*/ _jsx(Typography, {
        children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "xC5CQq",
            defaultMessage: [
                {
                    type: 0,
                    value: "Faire covers most of the shipping costs for Insider Free shipping. On some orders, brands will cover the shipping costs if they have a Faire Direct relationship with you (meaning, they pay 0% commission to Faire when they receive orders from you). "
                },
                {
                    type: 8,
                    value: "LinkFormatting",
                    children: [
                        {
                            type: 0,
                            value: "Learn more"
                        }
                    ]
                }
            ],
            values: {
                LinkFormatting: getLinkFormatting("5")
            }
        })
    });
};
export var HowMuchDoesInsiderCostAnswerNoVat = function() {
    var _insiderMarketingPageData_logged_out_info_default_membership, _insiderMarketingPageData_logged_out_info;
    var trialingPeriodDays = useInsiderTrialingPeriod().trialingPeriodDays;
    var formatMoney = useFormatMoney().formatMoney;
    var isLoggedInRetailer = useIsLoggedInRetailer();
    var _useInsiderMarketingPageData = useInsiderMarketingPageData({
        enabled: !isLoggedInRetailer
    }), insiderMarketingPageData = _useInsiderMarketingPageData.data, isLoading = _useInsiderMarketingPageData.isLoading;
    var loggedOutInsiderDefaultMonthlyPrice = moneyOrZero(insiderMarketingPageData === null || insiderMarketingPageData === void 0 ? void 0 : (_insiderMarketingPageData_logged_out_info = insiderMarketingPageData.logged_out_info) === null || _insiderMarketingPageData_logged_out_info === void 0 ? void 0 : (_insiderMarketingPageData_logged_out_info_default_membership = _insiderMarketingPageData_logged_out_info.default_membership) === null || _insiderMarketingPageData_logged_out_info_default_membership === void 0 ? void 0 : _insiderMarketingPageData_logged_out_info_default_membership.cost);
    var _useIsEligibleForAnnualBillingQuery = useIsEligibleForAnnualBillingQuery({
        enabled: isLoggedInRetailer
    }), isEligibleForAnnualBillingData = _useIsEligibleForAnnualBillingQuery.data, isEligibleForAnnualBillingLoading = _useIsEligibleForAnnualBillingQuery.isLoading;
    var _useInsiderMembershipPrice = useInsiderMembershipPrice(), insiderMembershipStandardPrice = _useInsiderMembershipPrice.insiderMembershipStandardPrice, insiderAnnualMembershipMonthlyPrice = _useInsiderMembershipPrice.insiderAnnualMembershipMonthlyPrice, insiderAnnualMembershipPrice = _useInsiderMembershipPrice.insiderAnnualMembershipPrice;
    var hasMembershipTaxDisclaimer = useInsiderFeatureEligibility().hasMembershipTaxDisclaimer;
    var formattedDefaultMonthlyPrice = formatMoney({
        money: isLoggedInRetailer ? insiderMembershipStandardPrice : loggedOutInsiderDefaultMonthlyPrice
    });
    var formattedAnnualPriceMoney = formatMoney({
        money: insiderAnnualMembershipPrice
    });
    var formattedAnnualMonthlyPriceMoney = formatMoney({
        money: insiderAnnualMembershipMonthlyPrice
    });
    if (isLoading || isEligibleForAnnualBillingLoading) {
        return /*#__PURE__*/ _jsx(LoadingSpinner, {});
    }
    return (isEligibleForAnnualBillingData === null || isEligibleForAnnualBillingData === void 0 ? void 0 : isEligibleForAnnualBillingData.is_eligible_for_annual_billing) ? /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "kdy8hM",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "For a limited time, when you sign up for Insider, you'll receive a free "
                        },
                        {
                            type: 1,
                            value: "trialingDaysNumber"
                        },
                        {
                            type: 0,
                            value: "-day trial. After your trial ends, your paid membership will begin automatically, and you'll be charged the membership fee based on your selected plan. Your Insider membership will auto-renew as follows:"
                        }
                    ],
                    values: {
                        trialingDaysNumber: trialingPeriodDays
                    }
                })
            }),
            /*#__PURE__*/ _jsxs(Typography, {
                children: [
                    /*#__PURE__*/ _jsx("br", {}),
                    "\xa0•\xa0",
                    /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "x7mlS/",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Annual Plan: Renews every 365 days from your enrollment date (i.e., when your free trial ended) at "
                            },
                            {
                                type: 1,
                                value: "annualPriceMoney"
                            },
                            {
                                type: 0,
                                value: "/year (equivalent to "
                            },
                            {
                                type: 1,
                                value: "monthlyAnnualPriceMoney"
                            },
                            {
                                type: 0,
                                value: "/month)."
                            }
                        ],
                        values: {
                            annualPriceMoney: hasMembershipTaxDisclaimer ? /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    formattedAnnualPriceMoney,
                                    "*"
                                ]
                            }) : formattedAnnualPriceMoney,
                            monthlyAnnualPriceMoney: formattedAnnualMonthlyPriceMoney
                        }
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Typography, {
                children: [
                    "\xa0•\xa0",
                    /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "ESD/w5",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Monthly Plan: Renews every 30 days from your enrollment date at "
                            },
                            {
                                type: 1,
                                value: "monthlyPriceMoney"
                            },
                            {
                                type: 0,
                                value: "/month."
                            }
                        ],
                        values: {
                            monthlyPriceMoney: hasMembershipTaxDisclaimer ? /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    formattedDefaultMonthlyPrice,
                                    "*"
                                ]
                            }) : formattedDefaultMonthlyPrice
                        }
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Typography, {
                children: [
                    /*#__PURE__*/ _jsx("br", {}),
                    /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "bYB7iU",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Payment will be charged to your default payment method on Faire, such as your credit/debit card or bank account. At this time, no other forms of payment are accepted for Insider. Please note that if you've previously had an Insider membership, you won't be eligible for a free trial."
                            }
                        ]
                    })
                ]
            }),
            hasMembershipTaxDisclaimer ? /*#__PURE__*/ _jsxs(Typography, {
                children: [
                    /*#__PURE__*/ _jsx("br", {}),
                    /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "tkLvQj",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "*exclusive of applicable taxes"
                            }
                        ]
                    })
                ]
            }) : null
        ]
    }) : /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                children: hasMembershipTaxDisclaimer ? /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "axylYx",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "For a limited-time, when you sign-up for Insider you'll get a free "
                        },
                        {
                            type: 1,
                            value: "trialingDaysNumber"
                        },
                        {
                            type: 0,
                            value: "-day trial. After your trial ends, your membership is "
                        },
                        {
                            type: 1,
                            value: "membershipFeeNumber"
                        },
                        {
                            type: 0,
                            value: "/month (exclusive of applicable taxes)."
                        }
                    ],
                    values: {
                        trialingDaysNumber: trialingPeriodDays,
                        membershipFeeNumber: formattedDefaultMonthlyPrice
                    }
                }) : /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "BazmPh",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "For a limited-time, when you sign-up for Insider you'll get a free "
                        },
                        {
                            type: 1,
                            value: "trialingDaysNumber"
                        },
                        {
                            type: 0,
                            value: "-day trial. After your trial ends, your membership is "
                        },
                        {
                            type: 1,
                            value: "membershipFeeNumber"
                        },
                        {
                            type: 0,
                            value: "/month."
                        }
                    ],
                    values: {
                        trialingDaysNumber: trialingPeriodDays,
                        membershipFeeNumber: formattedDefaultMonthlyPrice
                    }
                })
            }),
            /*#__PURE__*/ _jsxs(Typography, {
                children: [
                    /*#__PURE__*/ _jsx("br", {}),
                    /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "MJKWMy",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Your Insider membership will auto-renew every 30 days from your enrollment date (i.e., when your free trial ended). Payment will be charged to your default method of payment on Faire, such as your credit/debit card or bank account. Unfortunately, we don\u2019t currently accept any other forms of payment for Insider other than your default payment method on file."
                            }
                        ]
                    })
                ]
            })
        ]
    });
};
export var HowMuchDoesInsiderCostAnswer = function() {
    var trialingPeriodDays = useInsiderTrialingPeriod().trialingPeriodDays;
    var formatMoney = useFormatMoney().formatMoney;
    var isLoggedInRetailer = useIsLoggedInRetailer();
    var _useInsiderMembershipPrice = useInsiderMembershipPrice(), insiderMembershipIncomingPrice = _useInsiderMembershipPrice.insiderMembershipIncomingPrice, insiderAnnualMembershipPrice = _useInsiderMembershipPrice.insiderAnnualMembershipPrice, insiderAnnualMembershipMonthlyPrice = _useInsiderMembershipPrice.insiderAnnualMembershipMonthlyPrice;
    var _useIsEligibleForAnnualBillingQuery = useIsEligibleForAnnualBillingQuery({
        enabled: isLoggedInRetailer
    }), isEligibleForAnnualBillingData = _useIsEligibleForAnnualBillingQuery.data, isLoading = _useIsEligibleForAnnualBillingQuery.isLoading;
    var formattedMonthlyPrice = formatMoney({
        money: insiderMembershipIncomingPrice
    });
    var formattedAnnualPriceMoney = formatMoney({
        money: insiderAnnualMembershipPrice
    });
    var formattedAnnualMonthlyPriceMoney = formatMoney({
        money: insiderAnnualMembershipMonthlyPrice
    });
    if (isLoading) {
        return /*#__PURE__*/ _jsx(LoadingSpinner, {});
    }
    return (isEligibleForAnnualBillingData === null || isEligibleForAnnualBillingData === void 0 ? void 0 : isEligibleForAnnualBillingData.is_eligible_for_annual_billing) ? /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "i80Ubp",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "For a limited time, when you sign up for Insider, you'll receive a free "
                        },
                        {
                            type: 1,
                            value: "trialingDaysNumber"
                        },
                        {
                            type: 0,
                            value: "-day trial. After your trial ends, your paid membership will begin automatically, and you'll be charged the membership fee based on your selected plan. Your Insider membership will auto-renew as follows:"
                        }
                    ],
                    values: {
                        trialingDaysNumber: trialingPeriodDays
                    }
                })
            }),
            /*#__PURE__*/ _jsxs(Typography, {
                children: [
                    /*#__PURE__*/ _jsx("br", {}),
                    "\xa0•\xa0",
                    /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "phEYzn",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Annual Plan: Renews every 365 days from your enrollment date (i.e., when your free trial ended) at "
                            },
                            {
                                type: 1,
                                value: "annualPriceMoney"
                            },
                            {
                                type: 0,
                                value: "*/year (equivalent to "
                            },
                            {
                                type: 1,
                                value: "monthlyAnnualPriceMoney"
                            },
                            {
                                type: 0,
                                value: "/month)."
                            }
                        ],
                        values: {
                            annualPriceMoney: formattedAnnualPriceMoney,
                            monthlyAnnualPriceMoney: formattedAnnualMonthlyPriceMoney
                        }
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Typography, {
                children: [
                    "\xa0•\xa0",
                    /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "ABolii",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Monthly Plan: Renews every 30 days from your enrollment date at "
                            },
                            {
                                type: 1,
                                value: "monthlyPriceMoney"
                            },
                            {
                                type: 0,
                                value: "*/month."
                            }
                        ],
                        values: {
                            monthlyPriceMoney: formattedMonthlyPrice
                        }
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Typography, {
                children: [
                    /*#__PURE__*/ _jsx("br", {}),
                    /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "PS2CFt",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Payment will be charged to your default payment method on Faire, such as your credit/debit card or bank account. At this time, no other forms of payment are accepted for Insider. Please note that if you've previously had an Insider membership, you won't be eligible for a free trial."
                            }
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Typography, {
                children: [
                    /*#__PURE__*/ _jsx("br", {}),
                    /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "HN9gdA",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "*exclusive of VAT"
                            }
                        ]
                    })
                ]
            })
        ]
    }) : /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "g+Px7W",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "For a limited-time, when you sign-up for Insider you\u2019ll get a free "
                        },
                        {
                            type: 1,
                            value: "trialingDaysNumber"
                        },
                        {
                            type: 0,
                            value: "-day trial. After your trial ends, your membership is "
                        },
                        {
                            type: 1,
                            value: "membershipFeeNumber"
                        },
                        {
                            type: 0,
                            value: "*/month."
                        }
                    ],
                    values: {
                        trialingDaysNumber: trialingPeriodDays,
                        membershipFeeNumber: formattedMonthlyPrice
                    }
                })
            }),
            /*#__PURE__*/ _jsx("br", {}),
            /*#__PURE__*/ _jsx(Typography, {
                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "MJKWMy",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Your Insider membership will auto-renew every 30 days from your enrollment date (i.e., when your free trial ended). Payment will be charged to your default method of payment on Faire, such as your credit/debit card or bank account. Unfortunately, we don\u2019t currently accept any other forms of payment for Insider other than your default payment method on file."
                        }
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("br", {}),
            /*#__PURE__*/ _jsx(Typography, {
                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "npIYhY",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "*exclusive of VAT"
                        }
                    ]
                })
            })
        ]
    });
};
var SelfServeSupportLinkFormatting = function(chunk) {
    return /*#__PURE__*/ _jsx(LinkButton, {
        linkTo: supportSelfServe({
            trigger_va: "true"
        }),
        children: chunk
    });
};
export var CancellationAnswer = function(param) {
    var _param_sectionNumber = param.sectionNumber, sectionNumber = _param_sectionNumber === void 0 ? "11" : _param_sectionNumber;
    return /*#__PURE__*/ _jsx(Typography, {
        children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "BI1AD5",
            defaultMessage: [
                {
                    type: 0,
                    value: "Yes! There are no commitments with Insider, so you can cancel anytime, without penalty. If you choose to cancel, your Insider benefits will last until the end of your billing cycle. To cancel, you can simply login to your Faire.com account and visit the \u201CInsider Membership\u201D tab in the \u201CAccount\u201D section under your profile icon. "
                },
                {
                    type: 8,
                    value: "LinkFormatting",
                    children: [
                        {
                            type: 0,
                            value: "Learn More"
                        }
                    ]
                }
            ],
            values: {
                LinkFormatting: getLinkFormatting(sectionNumber)
            }
        })
    });
};
export var FreeShippingAndDutiesAnswer = function() {
    return /*#__PURE__*/ _jsx(Typography, {
        children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "TDcYWP",
            defaultMessage: [
                {
                    type: 0,
                    value: "The brands selected for free shipping and free import duties are personalized for each retailer. We account for a few factors in deciding which brands have shipping and duties covered, including the brand\u2019s shipping location in proximity to you and the relevancy of the brand based on your expressed interest. Brands do not opt-in to the Insider program, and there is no way to request that a specific brand be included. "
                },
                {
                    type: 8,
                    value: "LinkFormatting",
                    children: [
                        {
                            type: 0,
                            value: "Learn more"
                        }
                    ]
                }
            ],
            values: {
                LinkFormatting: getLinkFormatting("5")
            }
        })
    });
};
export var useInsiderFAQStrings = function() {
    var strictLocalize = useStrictLocalization().strictLocalize;
    var freeShippingAndDutiesQuestion = strictLocalize({
        id: "wu+NCA",
        defaultMessage: [
            {
                type: 0,
                value: "How does Faire select which brands have free shipping and free import duties?"
            }
        ]
    });
    var specialGrandfatheringQuestion = strictLocalize({
        id: "t4PhDk",
        defaultMessage: [
            {
                type: 0,
                value: "Which European brands do I keep free shipping on if I sign up?"
            }
        ]
    });
    var specialGrandfatheringAnswer = strictLocalize({
        id: "vCOnor",
        defaultMessage: [
            {
                type: 0,
                value: "As a limited-time offer, you'll automatically keep free shipping and duties on all European brands you've ordered from by April 4. These benefits will last through the end of the year (2023). After that, you may continue to receive free shipping and duties on some of these brands with your Insider membership. Faire selects which brands are eligible for these benefits with your membership, based on several factors like the brand's shipping location."
            }
        ]
    });
    var coverageChangesQuestion = strictLocalize({
        id: "IiEK1y",
        defaultMessage: [
            {
                type: 0,
                value: "Does Insider free shipping coverage change?"
            }
        ]
    });
    var cancellationQuestion = strictLocalize({
        id: "Z4rqov",
        defaultMessage: [
            {
                type: 0,
                value: "Can I cancel my membership anytime?"
            }
        ]
    });
    var howMuchDoesInsiderCostQuestion = strictLocalize({
        id: "W6B1DY",
        defaultMessage: [
            {
                type: 0,
                value: "How much does Insider cost and how do payments work?"
            }
        ]
    });
    var shippingCostsQuestion = strictLocalize({
        id: "GXvTCG",
        defaultMessage: [
            {
                type: 0,
                value: "How are the costs of shipping covered?"
            }
        ]
    });
    var shippingCostsQuestionEUR = strictLocalize({
        id: "aCaaFb",
        defaultMessage: [
            {
                type: 0,
                value: "How are the costs of shipping and import duties covered?"
            }
        ]
    });
    var shippingCostsAnswerEUR = strictLocalize({
        id: "QH32Or",
        defaultMessage: [
            {
                type: 0,
                value: "Insider membership fees help us to cover the cost of offering free shipping and import duties on thousands of local and international brands. None of these costs are passed on to the brands themselves\u2014so you can rest assured that ordering from Insider brands supports their growth, while still allowing you to save on shipping and import duties costs."
            }
        ]
    });
    var howDoesFaireSelectBrandsQuestion = strictLocalize({
        id: "oaHh5P",
        defaultMessage: [
            {
                type: 0,
                value: "How does Faire select which brands have free shipping?"
            }
        ]
    });
    var phoneSupportQuestion = strictLocalize({
        id: "u1HyLu",
        defaultMessage: [
            {
                type: 0,
                value: "How does Faire phone support work?"
            }
        ]
    });
    return {
        freeShippingAndDutiesQuestion: freeShippingAndDutiesQuestion,
        specialGrandfatheringQuestion: specialGrandfatheringQuestion,
        specialGrandfatheringAnswer: specialGrandfatheringAnswer,
        coverageChangesQuestion: coverageChangesQuestion,
        cancellationQuestion: cancellationQuestion,
        howMuchDoesInsiderCostQuestion: howMuchDoesInsiderCostQuestion,
        shippingCostsQuestion: shippingCostsQuestion,
        shippingCostsQuestionEUR: shippingCostsQuestionEUR,
        shippingCostsAnswerEUR: shippingCostsAnswerEUR,
        howDoesFaireSelectBrandsQuestion: howDoesFaireSelectBrandsQuestion,
        phoneSupportQuestion: phoneSupportQuestion
    };
};
var LinkButton = styled(Button).attrs({
    tertiaryColor: Core.text.subdued,
    variant: "tertiary",
    target: "_blank"
}).withConfig({
    componentId: "sc-fa121c60-0"
})([
    "font-size:inherit;font-weight:inherit;font-family:inherit;line-height:inherit;"
]);
var StyledInsider = styled(Insider).withConfig({
    componentId: "sc-fa121c60-1"
})([
    "vertical-align:baseline;"
]);
